import getUrlParameter from '@rsos/base-utils/getUrlParameter';
import { SCORPIUS_KEY, PRODUCT_TOKEN } from '../actions/types';
import { captureExceptionSafe } from './captureExceptionSafe';

/**
 * Backwards-compatible mitigation for legacy auto-login links that still pass
 * `access_token` in the query string.
 *
 * We intentionally keep consuming that token so existing entry points do not
 * break, but we immediately remove only the sensitive `access_token` parameter
 * from the visible URL after it has been read. This reduces accidental token
 * disclosure through copied links, screenshots, browser history, and referrer
 * headers while preserving the current login flow. Other query params and the
 * hash fragment are preserved.
 */
const stripAccessTokenFromUrl = () => {
  if (
    typeof window === 'undefined' ||
    typeof window.history?.replaceState !== 'function'
  ) {
    return;
  }

  const url = new URL(window.location.href);

  if (!url.searchParams.has('access_token')) {
    return;
  }

  url.searchParams.delete('access_token');

  // Security context: the token may arrive through a legacy deep link, but it
  // should not remain in the address bar any longer than necessary because URL
  // tokens are easy to leak outside the app boundary.
  window.history.replaceState(
    {},
    '',
    `${url.pathname}${url.search}${url.hash}`,
  );
};

/**
 * Returns the Scorpius tokens from localStorage.
 * Existing stored tokens still take precedence. When storage is empty, legacy
 * auto-login links may bootstrap auth from `?access_token=...`; in that case
 * the token is consumed once and then scrubbed from the URL as a defense-in-
 * depth measure against URL-based credential exposure.
 * @returns {Object} tokens
 */
export const getTokens = () => {
  let tokenString = localStorage.getItem(SCORPIUS_KEY);
  const accessToken = getUrlParameter('access_token');
  if (!tokenString && accessToken) {
    const bootstrapTokens = { refresh_token: null, token: accessToken };
    tokenString = JSON.stringify(bootstrapTokens);
    // Persist the bootstrap token immediately because getTokens() runs while
    // the Redux store initializes, before app-level initialize() handlers run.
    // Without this, stripping the query param can leave downstream apps with
    // no token source by the time they call fetchUser().
    localStorage.setItem(SCORPIUS_KEY, tokenString);
    stripAccessTokenFromUrl();

    // Track when we create tokens with explicit null refresh_token from URL parameter
    captureExceptionSafe(
      new Error('Creating tokens with null refresh_token from URL parameter'),
      {
        tags: {
          component: 'tokens',
          operation: 'url-token-with-null-refresh',
        },
        extra: {
          accessTokenLength: accessToken ? accessToken.length : null,
          userAgent:
            typeof navigator !== 'undefined' ? navigator.userAgent : null,
        },
      },
    );
    tokenString = JSON.stringify({ refresh_token: null, token: accessToken });
  }
  if (!tokenString) {
    return null;
  }
  try {
    return JSON.parse(tokenString);
  } catch (error) {
    captureExceptionSafe(error, {
      tags: {
        component: 'tokens',
        operation: 'parse-stored-tokens',
      },
      extra: {
        tokenString: tokenString ? tokenString.substring(0, 100) : null,
        tokenStringLength: tokenString ? tokenString.length : null,
      },
    });
    localStorage.removeItem(SCORPIUS_KEY);
    return null;
  }
};

/**
 * Saves the Scorpius tokens to localStorage.
 * @param {Object} tokens
 * @param {string} tokens.token
 * @param {string} tokens.refresh_token
 */
export const setTokens = tokens => {
  localStorage.setItem(SCORPIUS_KEY, JSON.stringify(tokens));
};

/**
 * Deletes the tokens from localStorage.
 */
export const clearTokens = () => {
  localStorage.removeItem(SCORPIUS_KEY);
  localStorage.removeItem(PRODUCT_TOKEN);
};
